import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { cn } from '@/utils/cn'
import './column-sizing.css'

const KEY_STEP = 16

/**
 * The drag handle on a header cell's edge (the cell must be positioned:
 * relative or sticky). Drag to resize, double-click to reset, or focus it and
 * use ←/→. ``edge="left"`` for right-pinned columns.
 */
export function ColumnResizer({
  label,
  width,
  onResize,
  onReset,
  edge = 'right',
}: {
  label: string
  width: number
  onResize: (width: number) => void
  onReset: () => void
  edge?: 'left' | 'right'
}) {
  const [dragging, setDragging] = useState(false)
  const start = useRef({ x: 0, width: 0 })
  const direction = edge === 'left' ? -1 : 1
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      aria-valuenow={Math.round(width)}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      data-resizing={dragging || undefined}
      className={cn(
        'wave-col-resizer',
        edge === 'left' ? 'wave-col-resizer-left' : 'wave-col-resizer-right'
      )}
      onClick={event => event.stopPropagation()}
      onDoubleClick={event => {
        event.stopPropagation()
        onReset()
      }}
      onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return
        event.preventDefault()
        event.stopPropagation()
        event.currentTarget.setPointerCapture(event.pointerId)
        start.current = { x: event.clientX, width }
        setDragging(true)
      }}
      onPointerMove={(event: PointerEvent<HTMLDivElement>) => {
        if (!dragging) return
        onResize(
          start.current.width + direction * (event.clientX - start.current.x)
        )
      }}
      onPointerUp={(event: PointerEvent<HTMLDivElement>) => {
        if (!dragging) return
        event.currentTarget.releasePointerCapture(event.pointerId)
        setDragging(false)
      }}
      onPointerCancel={() => setDragging(false)}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
        event.preventDefault()
        event.stopPropagation()
        const sign = event.key === 'ArrowRight' ? 1 : -1
        onResize(width + direction * sign * KEY_STEP)
      }}
    />
  )
}
